import { formatEUR, parsePtNumber } from '../utils/money';

export const MIN_MONTHS = 1;
export const MAX_MONTHS = 12;
export const RECOMMENDED_MONTHS = 3;

export function clampMonths(value) {
  const n = Number.parseInt(String(value), 10);
  if (!Number.isFinite(n)) return RECOMMENDED_MONTHS;
  return Math.min(MAX_MONTHS, Math.max(MIN_MONTHS, n));
}

export function parseAmount(value) {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const str = String(value).trim();
  if (!str) return 0;
  // pt-PT user input uses comma as decimal separator (e.g. "1.215,12")
  if (str.includes(',')) return parsePtNumber(str) ?? 0;
  // API values use dot as decimal separator (standard JSON, e.g. "16166.13")
  const n = Number(str);
  return Number.isFinite(n) ? n : 0;
}

export function computeTotalTarget(avgMonthly, months) {
  const avg = parseAmount(avgMonthly);
  const m = clampMonths(months);
  if (avg <= 0) return 0;
  return Math.round(avg * m);
}

/** Payload PATCH /api/emergency-fund/config/ após o passo Meses. */
export function buildCustomEfConfigPayload(avgMonthly, months) {
  const m = clampMonths(months);
  const total = computeTotalTarget(avgMonthly, m);
  return {
    base_fund_target: '0.00',
    ef_custom_target: total.toFixed(2),
    ef_calculation_mode: 'custom',
    ef_months_of_coverage: m,
  };
}

export function formatMonths(value) {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  return Number(value).toLocaleString('pt-PT', { maximumFractionDigits: 1 });
}

export function formatTargetDate(monthsHorizon) {
  const horizon = Math.max(1, Number(monthsHorizon) || RECOMMENDED_MONTHS);
  const d = new Date();
  d.setMonth(d.getMonth() + horizon);
  return d.toLocaleDateString('pt-PT', { month: 'short', year: 'numeric' });
}

/**
 * Marco educativo conforme meses seleccionados (escala contínua 1–12).
 * Tiers mockup: ≤1 primeiro respiro · ≤3 segurança · ≤6 recomendado · else tranquilidade
 */
export function getMonthsMilestone(months, copy) {
  const m = clampMonths(months);
  if (m <= 1) return { ...copy.milestoneMin, key: 'breath', showRecommended: false };
  if (m <= 3) return { ...copy.milestoneBasic, key: 'basic', showRecommended: m === RECOMMENDED_MONTHS };
  if (m <= 6) return { ...copy.milestoneSolid, key: 'recommended', showRecommended: m === 6 };
  return { ...copy.milestoneExtended, key: 'calm', showRecommended: false };
}

/** Marcos fixos no slider (1 / 3 / 6 / 12). */
export const MONTH_MARKS = [
  { at: 1, key: 'breath' },
  { at: 3, key: 'basic' },
  { at: 6, key: 'recommended' },
  { at: 12, key: 'calm' },
];

/** Tick activo = marco do tier actual. */
export function getActiveMonthMark(months) {
  const m = clampMonths(months);
  if (m <= 1) return 1;
  if (m <= 3) return 3;
  if (m <= 6) return 6;
  return 12;
}

/** Meses de liquidez imediata (detalhe informativo, não um segundo alvo). */
export function liquidityMonthsOf(months) {
  const m = clampMonths(months);
  return Math.max(1, Math.round(m / 6));
}

/**
 * Repartição automática Base/FE — só informativa (mockup 1a).
 * immediate ≈ 1/6 dos meses (mín. 1); o resto “a render”.
 */
export function composeFundSplit(avgMonthly, months) {
  const spend = parseAmount(avgMonthly);
  const m = clampMonths(months);
  const totalEur = computeTotalTarget(spend, m);
  if (totalEur <= 0 || spend <= 0) {
    return {
      totalEur: 0,
      liquidityMonths: 0,
      immediateEur: 0,
      growingEur: 0,
    };
  }
  const liquidityMonths = liquidityMonthsOf(m);
  const immediateEur = Math.min(totalEur, Math.round(spend * liquidityMonths));
  return {
    totalEur,
    liquidityMonths,
    immediateEur,
    growingEur: Math.max(0, totalEur - immediateEur),
  };
}

const PLAN_IMMEDIATE_TYPES = new Set(['conta_corrente']);
const PLAN_YIELD_TYPES = new Set([
  'conta_remunerada',
  'conta_poupanca',
  'deposito_prazo_3m',
  'deposito_prazo_6m',
  'deposito_prazo_12m',
]);
const PLAN_SAFE_TYPES = new Set(['certificados_aforro', 'certificados_tesouro']);

/**
 * Agrupa tiers da API em 2–3 cartões por benefício (mockup 3a).
 * Se não houver conta à ordem (base_target=0), usa immediateEur de composeFundSplit.
 */
export function groupPlanBenefits(tiers, { immediateEur = 0, totalEur = 0 } = {}) {
  const list = Array.isArray(tiers) ? tiers : [];
  let fromApiImmediate = 0;
  let yieldEur = 0;
  let safeEur = 0;

  for (const tier of list) {
    const amount = parseAmount(tier.amount_eur);
    const type = tier.product_type;
    if (PLAN_IMMEDIATE_TYPES.has(type) || tier.liquidity === 'imediata') {
      fromApiImmediate += amount;
    } else if (PLAN_SAFE_TYPES.has(type)) {
      safeEur += amount;
    } else if (PLAN_YIELD_TYPES.has(type)) {
      yieldEur += amount;
    } else {
      yieldEur += amount;
    }
  }

  const total = totalEur > 0 ? totalEur : fromApiImmediate + yieldEur + safeEur;
  let immediate = fromApiImmediate > 0 ? fromApiImmediate : parseAmount(immediateEur);
  if (immediate > total && total > 0) immediate = total;

  const remainder = Math.max(0, total - immediate);
  const yieldSafeSum = yieldEur + safeEur;
  if (fromApiImmediate <= 0 && remainder > 0 && yieldSafeSum > 0) {
    const scale = remainder / yieldSafeSum;
    yieldEur = Math.round(yieldEur * scale);
    safeEur = Math.max(0, remainder - yieldEur);
  } else if (fromApiImmediate <= 0 && remainder > 0 && yieldSafeSum <= 0) {
    yieldEur = remainder;
    safeEur = 0;
  }

  const cards = [];
  if (immediate > 0) {
    cards.push({ id: 'immediate', amountEur: immediate });
  }
  if (yieldEur > 0) {
    cards.push({ id: 'yield', amountEur: yieldEur });
  }
  if (safeEur > 0) {
    cards.push({ id: 'safe', amountEur: safeEur });
  }
  return cards;
}

/**
 * Quantos meses o saldo já cobre (floor(saved/spend)).
 * @returns {number} 0 = arranque
 */
export function protectedMonthsFromSaved(saved, avgMonthly) {
  const spend = parseAmount(avgMonthly);
  const s = parseAmount(saved);
  if (spend <= 0 || s <= 0) return 0;
  return Math.floor(s / spend);
}

export function formatProtectedMonthsLabel(protectedMonths, copy) {
  const n = Number(protectedMonths) || 0;
  if (n <= 0) return copy.protectedStart;
  if (n === 1) return copy.protectedOne;
  return copy.protectedMany.replace('{n}', String(n));
}

/** Segmentos 0–10 para barra de progresso saved/target. */
export function progressSegments(saved, target, count = 10) {
  const t = parseAmount(target);
  const s = parseAmount(saved);
  const filled = t <= 0 ? 0 : Math.min(count, Math.round((s / t) * count));
  return Array.from({ length: count }, (_, i) => i < filled);
}

/**
 * Título do cartão de tranquilidade — alinhado ao passo Pronto do wizard.
 * @param {number} monthsCovered
 * @param {object} copy — statusTemplate + protectedStart/One/Many
 */
export function formatPeaceStatus(monthsCovered, copy) {
  const n = Math.max(0, Math.floor(Number(monthsCovered) || 0));
  const protectedLabel = formatProtectedMonthsLabel(n, copy);
  return (copy.statusTemplate || '').replace('{protected}', protectedLabel);
}

export function formatGoalGapRow(gap, template) {
  if (gap <= 0) return null;
  return template.replace('{gap}', formatEUR(gap));
}

export function computeFundTotals(health) {
  const saved =
    Number(health?.base_fund_total ?? 0) + Number(health?.emergency_fund_total ?? 0);
  const target = Number(health?.emergency_fund_target ?? 0);
  return { saved, target, gap: Math.max(0, target - saved) };
}

/**
 * Estima meses cobertos quando ainda não há 3 selfies (alocação vs meta).
 */
export function estimateMonthsFromAllocation(saved, targetEur, targetMonths) {
  const savedNum = Number(saved ?? 0);
  const target = Number(targetEur ?? 0);
  const months = Number(targetMonths ?? RECOMMENDED_MONTHS);
  if (savedNum <= 0 || target <= 0 || months <= 0) return 0;
  return (savedNum / target) * months;
}

/**
 * Meses a mostrar na UI — evita valores absurdos quando faltam despesas nos selfies.
 */
export function resolveDisplayMonthsCovered({
  health,
  storedTotal,
  targetEur,
  targetMonths,
  hasEnoughSelfies,
}) {
  const { saved, target } = health
    ? computeFundTotals(health)
    : { saved: 0, target: 0 };
  const displaySaved = saved > 0 ? saved : Number(storedTotal ?? 0);
  const displayTarget = target > 0 ? target : Number(targetEur ?? 0);
  const estimate = estimateMonthsFromAllocation(displaySaved, displayTarget, targetMonths);

  if (!health || !hasEnoughSelfies(health)) return estimate;

  const raw = Number(health.months_covered ?? 0);
  if (!Number.isFinite(raw) || raw <= 0) return estimate;
  if (raw > Number(targetMonths) * 3) return estimate;
  return raw;
}

/**
 * Soma saldos das contas alocadas na 1.ª vs última selfie do ano civil.
 */
export function computeYearProgressEur(snapshots, accountIds, referenceYear = new Date().getFullYear()) {
  if (!Array.isArray(snapshots) || !accountIds?.length) return null;

  const idSet = new Set(accountIds.map(String));
  const yearSnaps = snapshots
    .filter(
      (s) =>
        s.is_completed !== false &&
        Number(s.reference_year) === referenceYear &&
        Array.isArray(s.account_snapshots),
    )
    .sort(
      (a, b) =>
        Number(a.reference_year) - Number(b.reference_year) ||
        Number(a.reference_month) - Number(b.reference_month),
    );

  if (yearSnaps.length < 2) return null;

  const sumForSnap = (snap) =>
    snap.account_snapshots.reduce((acc, row) => {
      if (!idSet.has(String(row.account))) return acc;
      return acc + Number(row.balance_eur ?? 0);
    }, 0);

  const first = sumForSnap(yearSnaps[0]);
  const last = sumForSnap(yearSnaps[yearSnaps.length - 1]);
  const delta = last - first;
  return Number.isFinite(delta) ? delta : null;
}

export function encouragementMessage(monthsCovered, templates) {
  const months = Number(monthsCovered ?? 0);
  if (months >= 6) return templates.solid;
  if (months >= 3) {
    return (templates.building || '').replace('{months}', formatMonths(months));
  }
  if (months > 0) return templates.started;
  return templates.empty;
}
